import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Expresión cron de corrida diaria simple: "min hora * * *"
const CRON_DIARIO_REGEX = /^\s*(\d{1,2})\s+(\d{1,2})\s+\*\s+\*\s+\*\s*$/;

// "0 9 * * *" -> "09:00"; null si la expresión no es una corrida diaria simple
const cronAHora = (cronExpression) => {
    const m = (cronExpression || '').match(CRON_DIARIO_REGEX);
    if (!m) return null;
    const [, minuto, hora] = m;
    if (parseInt(hora, 10) > 23 || parseInt(minuto, 10) > 59) return null;
    return `${hora.padStart(2, '0')}:${minuto.padStart(2, '0')}`;
};

// "09:00" -> "0 9 * * *"
const horaACron = (hora) => {
    const [h, m] = hora.split(':').map(v => parseInt(v, 10));
    return `${m} ${h} * * *`;
};

/**
 * Modal de configuración de las alertas automáticas de diésel.
 * Permite ajustar el umbral de variación (%) y administrar los
 * destinatarios que reciben el correo de alerta.
 *
 * Props:
 *  - onClose:  cierra el modal
 *  - userName: nombre del usuario autenticado (queda en modificado_por)
 */
const DieselConfigModal = ({ onClose, userName }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [config, setConfig] = useState(null);

    // Umbral
    const [umbralInput, setUmbralInput] = useState('');
    const [savingUmbral, setSavingUmbral] = useState(false);
    const [umbralMsg, setUmbralMsg] = useState(null); // { tipo: 'success'|'danger', texto }

    // Hora de la corrida (cron)
    const [horaInput, setHoraInput] = useState('');
    const [savingHora, setSavingHora] = useState(false);
    const [horaMsg, setHoraMsg] = useState(null);

    // Destinatarios
    const [nuevoCorreo, setNuevoCorreo] = useState('');
    const [nuevoNombre, setNuevoNombre] = useState('');
    const [savingDestinatario, setSavingDestinatario] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [destMsg, setDestMsg] = useState(null);

    const fetchConfig = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await apiClient.get('/api/diesel/config');
            setConfig(data);
            setUmbralInput(String(data.umbralPct));
            setHoraInput(cronAHora(data.cronExpression) || '');
        } catch (err) {
            console.error('Error al cargar la configuración de diésel:', err);
            setError(err.response?.data?.error || 'No se pudo cargar la configuración');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    // Cerrar con Escape
    useEffect(() => {
        const onKeyDown = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const umbralValido = () => {
        const v = parseFloat(umbralInput);
        return !isNaN(v) && v > 0 && v <= 100;
    };

    const handleGuardarUmbral = async () => {
        if (!umbralValido()) return;
        setSavingUmbral(true);
        setUmbralMsg(null);
        try {
            const { data } = await apiClient.put('/api/diesel/config', {
                umbralPct: parseFloat(umbralInput),
                modificadoPor: userName,
            });
            setConfig(prev => ({ ...prev, umbralPct: data.umbralPct }));
            setUmbralMsg({ tipo: 'success', texto: 'Umbral actualizado correctamente' });
        } catch (err) {
            console.error('Error al actualizar el umbral:', err);
            setUmbralMsg({ tipo: 'danger', texto: err.response?.data?.error || 'Error al actualizar el umbral' });
        } finally {
            setSavingUmbral(false);
        }
    };

    const handleGuardarHora = async () => {
        if (!horaInput) return;
        setSavingHora(true);
        setHoraMsg(null);
        try {
            const cronExpression = horaACron(horaInput);
            const { data } = await apiClient.put('/api/diesel/config', {
                cronExpression,
                modificadoPor: userName,
            });
            setConfig(prev => ({ ...prev, cronExpression: data.cronExpression }));
            setHoraMsg({ tipo: 'success', texto: 'Hora actualizada. Aplicará cuando se reinicie el proceso del monitor.' });
        } catch (err) {
            console.error('Error al actualizar la hora de la corrida:', err);
            setHoraMsg({ tipo: 'danger', texto: err.response?.data?.error || 'Error al actualizar la hora' });
        } finally {
            setSavingHora(false);
        }
    };

    const handleAgregarDestinatario = async () => {
        const correo = nuevoCorreo.trim().toLowerCase();
        if (!EMAIL_REGEX.test(correo)) {
            setDestMsg({ tipo: 'danger', texto: 'Captura un correo con formato válido' });
            return;
        }
        setSavingDestinatario(true);
        setDestMsg(null);
        try {
            const { data } = await apiClient.post('/api/diesel/config/destinatarios', {
                correo,
                nombre: nuevoNombre.trim() || null,
            });
            setConfig(prev => ({ ...prev, destinatarios: data.destinatarios }));
            setNuevoCorreo('');
            setNuevoNombre('');
            setDestMsg({ tipo: 'success', texto: `Se agregó ${correo}` });
        } catch (err) {
            console.error('Error al agregar destinatario:', err);
            setDestMsg({ tipo: 'danger', texto: err.response?.data?.error || 'Error al agregar el destinatario' });
        } finally {
            setSavingDestinatario(false);
        }
    };

    const handleRetirarDestinatario = async (dest) => {
        if (!window.confirm(`¿Retirar a ${dest.correo} de las alertas de diésel?`)) return;
        setRemovingId(dest.idDestinatario);
        setDestMsg(null);
        try {
            const { data } = await apiClient.delete(`/api/diesel/config/destinatarios/${dest.idDestinatario}`);
            setConfig(prev => ({ ...prev, destinatarios: data.destinatarios }));
            setDestMsg({ tipo: 'success', texto: `Se retiró ${dest.correo}` });
        } catch (err) {
            console.error('Error al retirar destinatario:', err);
            setDestMsg({ tipo: 'danger', texto: err.response?.data?.error || 'Error al retirar el destinatario' });
        } finally {
            setRemovingId(null);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '0.35rem', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)' }}>
                <div className="modal-header bg-primary">
                    <h5 className="modal-title text-white font-weight-bold">
                        <i className="fas fa-cogs mr-2"></i>Configuración de alertas de diésel
                    </h5>
                    <button type="button" className="close text-white" onClick={onClose}>
                        <span>&times;</span>
                    </button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger d-flex justify-content-between align-items-center mb-0">
                            <span><i className="fas fa-exclamation-triangle mr-2"></i>{error}</span>
                            <button className="btn btn-sm btn-outline-danger" onClick={fetchConfig}>Reintentar</button>
                        </div>
                    ) : (
                        <>
                            {/* ── Umbral de variación ─────────────────────────── */}
                            <h6 className="font-weight-bold text-primary mb-2">
                                <i className="fas fa-percentage mr-2"></i>Umbral de variación
                            </h6>
                            <p className="small text-muted mb-2">
                                Se enviará la alerta cuando el precio promedio del diésel varíe
                                (a la alza o a la baja) más de este porcentaje respecto a la corrida anterior.
                            </p>
                            <div className="form-row align-items-center mb-2">
                                <div className="col-auto">
                                    <div className="input-group" style={{ width: '160px' }}>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min="0.01"
                                            max="100"
                                            step="0.5"
                                            value={umbralInput}
                                            onChange={e => setUmbralInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleGuardarUmbral(); }}
                                        />
                                        <div className="input-group-append">
                                            <span className="input-group-text">%</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <button
                                        className="btn btn-primary btn-sm font-weight-bold"
                                        onClick={handleGuardarUmbral}
                                        disabled={savingUmbral || !umbralValido() || parseFloat(umbralInput) === config.umbralPct}
                                    >
                                        {savingUmbral
                                            ? <><i className="fas fa-spinner fa-spin mr-1"></i>Guardando...</>
                                            : <><i className="fas fa-save mr-1"></i>Guardar</>}
                                    </button>
                                </div>
                            </div>
                            {umbralMsg && (
                                <div className={`alert alert-${umbralMsg.tipo} py-1 px-2 small mb-2`}>{umbralMsg.texto}</div>
                            )}
                            <p className="small text-muted mb-3">
                                Umbral vigente: <b>{config.umbralPct}%</b>
                            </p>

                            <hr />

                            {/* ── Hora de la corrida diaria ───────────────────── */}
                            <h6 className="font-weight-bold text-primary mb-2">
                                <i className="fas fa-clock mr-2"></i>Hora de la corrida diaria
                            </h6>
                            <p className="small text-muted mb-2">
                                Hora a la que el monitor consulta los precios cada día. El cambio
                                se guarda en la base de datos y <b>aplica al reiniciar el proceso del monitor</b>.
                            </p>
                            {cronAHora(config.cronExpression) === null && (
                                <div className="alert alert-warning py-1 px-2 small mb-2">
                                    <i className="fas fa-exclamation-triangle mr-1"></i>
                                    La programación actual es una expresión cron personalizada
                                    (<code>{config.cronExpression}</code>). Si guardas una hora aquí,
                                    se reemplazará por una corrida diaria a esa hora.
                                </div>
                            )}
                            <div className="form-row align-items-center mb-2">
                                <div className="col-auto">
                                    <input
                                        type="time"
                                        className="form-control"
                                        style={{ width: '160px' }}
                                        value={horaInput}
                                        onChange={e => setHoraInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleGuardarHora(); }}
                                    />
                                </div>
                                <div className="col-auto">
                                    <button
                                        className="btn btn-primary btn-sm font-weight-bold"
                                        onClick={handleGuardarHora}
                                        disabled={savingHora || !horaInput || horaACron(horaInput) === config.cronExpression}
                                    >
                                        {savingHora
                                            ? <><i className="fas fa-spinner fa-spin mr-1"></i>Guardando...</>
                                            : <><i className="fas fa-save mr-1"></i>Guardar</>}
                                    </button>
                                </div>
                            </div>
                            {horaMsg && (
                                <div className={`alert alert-${horaMsg.tipo} py-1 px-2 small mb-2`}>{horaMsg.texto}</div>
                            )}
                            <p className="small text-muted mb-3">
                                Corrida programada: {cronAHora(config.cronExpression)
                                    ? <b>todos los días a las {cronAHora(config.cronExpression)} h</b>
                                    : <code>{config.cronExpression}</code>}
                            </p>

                            <hr />

                            {/* ── Destinatarios ───────────────────────────────── */}
                            <h6 className="font-weight-bold text-primary mb-2">
                                <i className="fas fa-envelope mr-2"></i>Destinatarios de la alerta
                            </h6>

                            {config.destinatarios.length === 0 ? (
                                <div className="alert alert-warning py-2 small">
                                    <i className="fas fa-exclamation-triangle mr-2"></i>
                                    No hay destinatarios: aunque se supere el umbral, <b>no se enviará ningún correo</b>.
                                </div>
                            ) : (
                                <table className="table table-sm table-hover mb-3">
                                    <thead>
                                        <tr className="small text-muted">
                                            <th>Nombre</th>
                                            <th>Correo</th>
                                            <th style={{ width: '60px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {config.destinatarios.map(dest => (
                                            <tr key={dest.idDestinatario}>
                                                <td className="align-middle">{dest.nombre || <span className="text-muted">—</span>}</td>
                                                <td className="align-middle">{dest.correo}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-outline-danger btn-sm"
                                                        title="Retirar destinatario"
                                                        onClick={() => handleRetirarDestinatario(dest)}
                                                        disabled={removingId === dest.idDestinatario}
                                                    >
                                                        {removingId === dest.idDestinatario
                                                            ? <i className="fas fa-spinner fa-spin"></i>
                                                            : <i className="fas fa-user-minus"></i>}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            <div className="form-row mb-2">
                                <div className="col-5">
                                    <input
                                        type="email"
                                        className="form-control form-control-sm"
                                        placeholder="correo@dominio.com"
                                        value={nuevoCorreo}
                                        onChange={e => setNuevoCorreo(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleAgregarDestinatario(); }}
                                    />
                                </div>
                                <div className="col-4">
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Nombre (opcional)"
                                        value={nuevoNombre}
                                        onChange={e => setNuevoNombre(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleAgregarDestinatario(); }}
                                    />
                                </div>
                                <div className="col-3">
                                    <button
                                        className="btn btn-success btn-sm btn-block font-weight-bold"
                                        onClick={handleAgregarDestinatario}
                                        disabled={savingDestinatario || !EMAIL_REGEX.test(nuevoCorreo.trim())}
                                    >
                                        {savingDestinatario
                                            ? <><i className="fas fa-spinner fa-spin mr-1"></i></>
                                            : <><i className="fas fa-user-plus mr-1"></i>Agregar</>}
                                    </button>
                                </div>
                            </div>
                            {destMsg && (
                                <div className={`alert alert-${destMsg.tipo} py-1 px-2 small mb-0`}>{destMsg.texto}</div>
                            )}
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn btn-secondary btn-sm" onClick={onClose}>Cerrar</button>
                </div>
            </div>
        </div>
    );
};

export default DieselConfigModal;
